import React, { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Alert, Slider } from '@mui/material';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { readFile, getCroppedSquarePng } from '../../../utils/ImageUtils';

// 192 px: múltiplo de 48 (lo que pide Google), sirve para la pestaña y para el ícono del celular (180 px).
const FAVICON_SIZE = 192;
const FAVICON_FOLDER = 'favicon';
const MAX_SOURCE_BYTES = 10 * 1024 * 1024;

const FaviconUploadDialog = ({ open, onClose, onUploaded }) => {
  const { t } = useTranslation();
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [tooSmall, setTooSmall] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setPixels(null);
      setTooSmall(false);
    }
  }, [open]);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      SnackbarUtils.error(t('invalid_image_format') || 'El archivo debe ser una imagen');
      return;
    }
    if (file.size > MAX_SOURCE_BYTES) {
      SnackbarUtils.error(t('file_too_large') || 'El archivo es demasiado grande');
      return;
    }
    setImageSrc(await readFile(file));
  };

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setPixels(croppedAreaPixels);
    setTooSmall(croppedAreaPixels.width < FAVICON_SIZE);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !pixels) return;
    try {
      setUploading(true);
      const file = await getCroppedSquarePng(imageSrc, pixels, FAVICON_SIZE);
      const uploaded = await SitesService.uploadImage(file, FAVICON_FOLDER);
      onUploaded(uploaded.s3Key);
      onClose();
    } catch (error) {
      console.error('Error uploading favicon:', error);
      SnackbarUtils.error(
        error.response?.data?.message || error.message || t('error_uploading_image') || 'Error al subir la imagen'
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={uploading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('upload_favicon') || 'Subir favicon'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('favicon_upload_hint')}
        </Typography>
        <Button variant="outlined" component="label" disabled={uploading}>
          {t('favicon_choose_file') || 'Elegir imagen'}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={handleFile} />
        </Button>
        {imageSrc && (
          <>
            <Box sx={{ position: 'relative', width: '100%', height: 280, mt: 2 }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </Box>
            <Slider min={1} max={4} step={0.05} value={zoom} onChange={(_, v) => setZoom(v)} sx={{ mt: 2 }} />
            {tooSmall && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {t('favicon_small_source')}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={uploading}>{t('cancel') || 'Cancelar'}</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!imageSrc || !pixels || uploading}>
          {uploading ? (t('uploading') || 'Subiendo...') : (t('favicon_save_and_use') || 'Recortar y usar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FaviconUploadDialog;
